import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  ViewStyle,
  TouchableOpacityProps,
  TextStyle,
} from 'react-native';

import {
  useCurrentVisibleDate,
  useSetCurrentVisibleDate,
} from './booking/SlotsAPIProvider';
import { TextBodySmall } from '../../components/StyledText';
import Colors from '../../constants/Colors';
import useColorScheme from '../../hooks/useColorScheme';
import { getNextMonthDate } from '../../utils/date';
import { ArrowBackIcon, ArrowForwardIcon } from '../icons';

const daysInMonth = (month: number, year: number) =>
  new Date(year, month + 1, 0).getDate();

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const daysOfWeek = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

type DayInfo = {
  dayNumber: string;
  type: 'prev' | 'current' | 'next';
  dateInfo: {
    dateString: string;
    dayOfWeek: number;
  };
};

// makes number two character (01-12)
const prefixZero = (n: number) => ('0' + n).slice(-2);

type GetDayCellStyleFn = (
  datestring: string
) => undefined | Partial<DayCellStyles>;

type DayCellStyles = {
  id?: string;
  rangeStart: boolean;
  rangeEnd: boolean;
  selected: boolean;
};

type ComponentStyles = Partial<{
  headerText?: TextStyle;
  header?: ViewStyle;
  cellText?: TextStyle;
  dayCell?: ViewStyle;
  daysOfWeek?: ViewStyle;
  daysOfWeekText?: TextStyle;
  arrow?: ViewStyle;
  arrowLeft?: ViewStyle;
  arrowRight?: ViewStyle;
  navButton?: ViewStyle;
}>;

type BookingCalendarProps =
  | {
      displayMode: 'weekly';
      onSelect: (id: string) => void;
      availableWeekIds: string[];
      selectedWeekIds: string[];
      isDayAvailable?: (datestring: string) => boolean;
      getDayDecorationAmount?: never;
      getDayCellStyle?: never;
      initialDate?: Date;
      forceDate?: boolean;
      getDayStyle?: (datestring: string) => undefined | ViewStyle;
      style?: ViewStyle;
      componentStyles?: ComponentStyles;
      disabled?: boolean;
      crossMonth: never;
      crossMonthDates: never;
      customTitle?: string;
    }
  | {
      displayMode: 'daily';
      onSelect: (id: string) => void;
      isDayAvailable?: (datestring: string) => boolean;
      availableWeekIds?: never;
      selectedWeekIds?: never;
      getDayDecorationAmount?: (datestring: string) => number;
      getDayCellStyle?: GetDayCellStyleFn;
      initialDate?: Date;
      forceDate?: boolean;
      getDayStyle?: (datestring: string) => undefined | ViewStyle;
      style?: ViewStyle;
      componentStyles?: ComponentStyles;
      disabled?: boolean;
      customTitle?: string;
      crossMonth?: boolean;
      crossMonthDates?: {
        startDate: string;
        endDate: string;
      };
    };

const useCurrentMonth = (initialDate: Date = new Date()) => {
  const syncedDate = useCurrentVisibleDate();
  const setSyncedDate = useSetCurrentVisibleDate();

  const sourceDate = syncedDate || initialDate;

  const [currentDate, setCurrentDate] = useState(
    new Date(sourceDate.getFullYear(), sourceDate.getMonth(), 1)
  );

  useEffect(() => {
    setSyncedDate(currentDate);
  }, [currentDate]);

  const changeMonth = (n: -1 | 1) => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + n, 1)
    );
  };

  return {
    currentDate,
    changeMonth,
  } as const;
};

const BookingCalendar = ({
  displayMode,
  onSelect,
  isDayAvailable = () => true,
  availableWeekIds,
  selectedWeekIds,
  getDayDecorationAmount = undefined,
  getDayCellStyle = () => undefined,
  getDayStyle,
  initialDate,
  forceDate,
  style,
  componentStyles,
  disabled,
  crossMonth,
  crossMonthDates,
  customTitle,
}: BookingCalendarProps) => {
  const { currentDate: currentSyncedDate, changeMonth } =
    useCurrentMonth(initialDate);

  const currentDate = forceDate
    ? initialDate ?? currentSyncedDate
    : currentSyncedDate;

  const renderHeader = () => {
    return (
      <View style={[styles.header, componentStyles?.header]}>
        <TouchableOpacity
          onPress={() => changeMonth(-1)}
          style={[styles.navButton, componentStyles?.navButton]}
        >
          <ArrowBackIcon
            style={[componentStyles?.arrow, componentStyles?.arrowLeft]}
          />
        </TouchableOpacity>
        <TextBodySmall
          style={[styles.headerText, componentStyles?.headerText]}
          forceStyles
        >
          {customTitle ||
            `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`}
        </TextBodySmall>
        <TouchableOpacity
          onPress={() => changeMonth(1)}
          style={[styles.navButton, componentStyles?.navButton]}
        >
          <ArrowForwardIcon
            style={[componentStyles?.arrow, componentStyles?.arrowRight]}
          />
        </TouchableOpacity>
      </View>
    );
  };

  const renderDaysOfWeek = () => (
    <View style={[styles.daysOfWeek, componentStyles?.daysOfWeek]}>
      {daysOfWeek.map((day, index) => (
        <TextBodySmall
          key={index}
          style={[styles.dayOfWeek, componentStyles?.daysOfWeekText]}
        >
          {day}
        </TextBodySmall>
      ))}
    </View>
  );

  const [pressedGroupId, setPressedGroupId] = useState<string | null>(null);
  const renderDays = () => {
    let daysArray = getDaysArray(currentDate).concat(
      crossMonth ? getDaysArray(getNextMonthDate(currentDate)) : []
    );

    if (crossMonth && crossMonthDates) {
      const firstCrossDayIndex = daysArray.findIndex(
        (day) => day.dateInfo.dateString === crossMonthDates.startDate
      );
      const lastCrossDayIndex = daysArray.findIndex(
        (day) => day.dateInfo.dateString === crossMonthDates.endDate
      );
      // concat weeksBeforeCross and weeksAfterCross
      daysArray = daysArray.slice(
        firstCrossDayIndex - (firstCrossDayIndex % 7) - 14,
        lastCrossDayIndex + (7 - (lastCrossDayIndex % 7)) + 14
      );
    }

    const elements =
      displayMode === 'daily'
        ? daysArray.map((dayObject) => (
            <DayCell
              key={`${dayObject.type}-${dayObject.dateInfo.dateString}`}
              label={dayObject.dayNumber}
              disabled={!isDayAvailable(dayObject.dateInfo.dateString)}
              onPress={
                disabled
                  ? undefined
                  : () => onSelect(dayObject.dateInfo.dateString)
              }
              dayDecorationAmount={getDayDecorationAmount?.(
                dayObject.dateInfo.dateString
              )}
              cellStyles={getDayCellStyle(dayObject.dateInfo.dateString)}
              style={getDayStyle?.(dayObject.dateInfo.dateString)}
              textStyle={componentStyles?.cellText}
              customDayStyle={componentStyles?.dayCell}
              {...{ pressedGroupId, setPressedGroupId }}
            />
          ))
        : getWeeklyElements(
            daysArray,
            isDayAvailable,
            availableWeekIds,
            selectedWeekIds,
            onSelect
          );
    return <View style={styles.days}>{elements}</View>;
  };

  return (
    <View style={[styles.container, style]}>
      {renderHeader()}
      {renderDaysOfWeek()}
      {renderDays()}
    </View>
  );
};

function getDaysArray(currentDate: Date) {
  const daysArray: DayInfo[] = [];
  const startDay = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  ).getDay();
  const totalDays = daysInMonth(
    currentDate.getMonth(),
    currentDate.getFullYear()
  );

  let prevYear = currentDate.getFullYear();

  const previousMonthDays = daysInMonth(
    currentDate.getMonth() - 1,
    currentDate.getFullYear()
  );
  const prevMonthHumanMonth =
    currentDate.getMonth() - 1 < 0 ? 12 : currentDate.getMonth();
  if (prevMonthHumanMonth === 12) {
    prevYear -= 1;
  }
  for (let i = startDay - 1; i >= 0; i--) {
    const dayOfWeek = startDay - 1 - i;
    daysArray.push({
      dayNumber: `${previousMonthDays - i}`,
      type: 'prev',
      dateInfo: {
        dateString: [
          prevYear,
          prefixZero(prevMonthHumanMonth),
          prefixZero(previousMonthDays - i),
        ].join('-'),
        dayOfWeek,
      },
    });
  }

  // Add current month days
  const currentMonthAsString = prefixZero(currentDate.getMonth() + 1);
  for (let i = 1; i <= totalDays; i++) {
    const dayOfWeek = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      i
    ).getDay();
    daysArray.push({
      dayNumber: `${i}`,
      type: 'current',

      dateInfo: {
        dateString: [
          currentDate.getFullYear(),
          currentMonthAsString,
          prefixZero(i),
        ].join('-'),
        dayOfWeek,
      },
    });
  }
  // Add succeeding days from the next month
  const lastDayOfWeekOfCurrentMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    totalDays
  ).getDay();
  let year = currentDate.getFullYear();
  let nextMonth = currentDate.getMonth() + 2;
  if (nextMonth > 12) {
    nextMonth = nextMonth - 12;
    year += 1;
  }
  const nextMonthAsString = prefixZero(nextMonth);
  const remainingDays = (7 - (daysArray.length % 7)) % 7;
  for (let i = 1; i <= remainingDays; i++) {
    const dayOfWeek = lastDayOfWeekOfCurrentMonth + i;
    daysArray.push({
      dayNumber: `${i}`,
      type: 'next',

      dateInfo: {
        dateString: [year, nextMonthAsString, prefixZero(i)].join('-'),
        dayOfWeek,
      },
    });
  }
  return daysArray;
}

function getWeeklyElements(
  daysArray: DayInfo[],
  isDayAvailable: (datestring: string) => boolean,
  availableWeekIds: string[],
  selectedWeekIds: string[],
  onSelect: (datestring: string) => void
) {
  const els: JSX.Element[] = [];

  for (let i = 0; i < daysArray.length; i++) {
    const dayObject = daysArray[i];
    if (!dayObject) continue;

    // start wrapping week from monday
    if (dayObject.dateInfo.dayOfWeek === 1) {
      // -1 and +5 is being caused by the way we generate weekIds in getWeekInfo() function
      // sunday:saturday
      const weekId = [
        daysArray[i - 1]!.dateInfo.dateString,
        daysArray[i + 5]!.dateInfo.dateString,
      ].join(':');
      const weekDisabled = !availableWeekIds.includes(weekId);
      const isWeekSelected = selectedWeekIds.includes(weekId);

      const days: JSX.Element[] = [];
      for (let d = 0; d < 5; d++) {
        const currentDayObject = daysArray[i + d];
        if (!currentDayObject) break;
        days.push(
          <DayCell
            key={`${currentDayObject.type}-${currentDayObject.dayNumber}`}
            style={{ width: '20%' }}
            weekEntry={!weekDisabled}
            weekLeft={d === 0}
            weekRight={d === 4}
            weekSelected={isWeekSelected}
            label={currentDayObject.dayNumber}
            disabled={!isDayAvailable(currentDayObject.dateInfo.dateString)}
          />
        );
      }
      // main week button
      els.push(
        <TouchableOpacity
          style={styles.pressableWeek}
          disabled={weekDisabled}
          onPress={() => onSelect(weekId)}
          key={`week-${weekId}`}
        >
          {days}
        </TouchableOpacity>
      );
      // skip to saturday
      i += 4;
    } else {
      els.push(
        <DayCell
          key={`${dayObject.type}-${dayObject.dayNumber}`}
          label={dayObject.dayNumber}
          weekEntry={false}
          disabled={!isDayAvailable(dayObject.dateInfo.dateString)}
        />
      );
    }
  }
  return els;
}

function DayCell({
  label,
  weekEntry,
  weekLeft,
  weekRight,
  weekSelected = false,
  disabled,
  style,
  onPress,
  dayDecorationAmount = 0,
  cellStyles,
  pressedGroupId,
  setPressedGroupId,
  textStyle,
  customDayStyle,
}: {
  label: string;
  weekEntry?: boolean;
  weekLeft?: boolean;
  weekRight?: boolean;
  disabled?: boolean;
  weekSelected?: boolean;
  onPress?: () => void;
  style?: ViewStyle;
  textStyle?: TextStyle;
  dayDecorationAmount?: number;
  cellStyles?: Partial<DayCellStyles>;
  pressedGroupId?: string | null;
  setPressedGroupId?: (groupId: string | null) => void;
  customDayStyle?: ViewStyle;
}) {
  const theme = useColorScheme();
  const backgroundColor = Colors[theme].backgroundSecondary;

  const shouldHaveBG =
    weekEntry ||
    cellStyles?.rangeStart ||
    cellStyles?.rangeEnd ||
    cellStyles?.selected;

  const cmp = (
    <View
      style={[
        onPress && {
          width: '100%',
          height: '100%',
          alignItems: 'center',
          justifyContent: 'center',
        },
        !onPress && styles.day,
        !onPress && customDayStyle,
        shouldHaveBG ? { backgroundColor } : {},
        (weekLeft || cellStyles?.rangeStart) && styles.weekLeftRadius,
        (weekRight || cellStyles?.rangeEnd) && styles.weekRightRadius,
        weekSelected && styles.selectedDayCell,
        style,
      ]}
    >
      <TextBodySmall
        style={[
          styles.dayText,
          disabled
            ? styles.dayDisabledText
            : {
                fontWeight: 'bold',
                color: Colors[theme].accentTertiary,
              },
          textStyle,
        ]}
      >
        {label}
      </TextBodySmall>
      {dayDecorationAmount > 0 ? (
        <View
          style={{
            flexDirection: 'row',
            position: 'absolute',
            bottom: 3,
            left: 0,
            right: 0,
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {Array(1)
            .fill(1)
            .map((_, idx) => (
              <View
                key={idx}
                style={{
                  width: 4,
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: Colors[theme].textSecondary,
                }}
              />
            ))}
        </View>
      ) : null}
    </View>
  );
  const cellGroupId = cellStyles?.id;
  const setPressedGroupIdFn = useCallback(() => {
    if (!cellGroupId) return;
    setPressedGroupId?.(cellGroupId);
  }, [cellGroupId, setPressedGroupId]);
  const resetPressedGroupIdFn = useCallback(() => {
    setPressedGroupId?.(null);
  }, [setPressedGroupId]);
  const touchableOpacityProps: TouchableOpacityProps = useMemo(
    () =>
      cellGroupId
        ? {
            activeOpacity: 1,
            onPressIn: setPressedGroupIdFn,
            onPressOut: resetPressedGroupIdFn,
            style: [
              styles.day,
              pressedGroupId === cellGroupId
                ? {
                    opacity: 0.5,
                  }
                : undefined,
            ],
          }
        : {},
    [pressedGroupId, cellGroupId]
  );

  return onPress ? (
    <TouchableOpacity
      style={[styles.day, customDayStyle]}
      onPress={onPress}
      disabled={disabled}
      {...touchableOpacityProps}
    >
      {cmp}
    </TouchableOpacity>
  ) : (
    cmp
  );
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: 20,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  headerText: {
    paddingTop: 10,
    height: 30,
    fontSize: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  navButton: {
    height: 30,
    width: '14.28%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  daysOfWeek: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    height: 30,
  },
  dayOfWeek: {
    width: '14.28%',
    textAlign: 'center',
  },
  days: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  day: {
    width: '14.28%',
    height: 36,
    justifyContent: 'center',
    alignItems: 'center',
    marginVertical: 2,
    position: 'relative',
  },
  weekLeftRadius: {
    borderBottomLeftRadius: 18,
    borderTopLeftRadius: 18,
  },
  weekRightRadius: {
    borderBottomRightRadius: 18,
    borderTopRightRadius: 18,
  },
  pressableWeek: { width: '71.4%', flexDirection: 'row' },
  selectedDayCell: { backgroundColor: '#F8C1D9' },
  dayText: {
    fontSize: 16,
  },
  dayDisabledText: {
    textDecorationLine: 'line-through',
    textDecorationStyle: 'solid',
    color: 'rgba(137, 130, 121, 0.4)',
  },
});

export default BookingCalendar;
